import { useEffect, useState, type FormEvent } from "react";
import { toast } from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { affiliateService } from "@/services/affiliate.service";
import { useAffiliate } from "@/contexts/affiliate/hooks";

interface EmailVerificationBannerProps {
  affiliateId: string;
  email: string;
  status: "pending" | "success";
  onVerified?: () => void;
}

/** One line: the address, a field for the emailed code, and a way to resend it. */
export function EmailVerificationBanner({ affiliateId, email, status, onVerified }: EmailVerificationBannerProps) {
  const { refetchAffiliate } = useAffiliate();
  const [resending, setResending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState("");
  const [state, setState] = useState(status);

  useEffect(() => setState(status), [status]);

  const resend = async () => {
    setResending(true);
    try {
      await affiliateService.resendVerificationEmail(affiliateId);
      toast.success("Code sent");
    } catch {
      toast.error("Could not send the code");
    } finally {
      setResending(false);
    }
  };

  const verify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      toast.error("Enter the 6-digit code from the email");
      return;
    }
    setVerifying(true);
    try {
      await affiliateService.verifyEmailCode(email, code.trim());
      toast.success("Email verified");
      setCode("");
      setState("success");
      onVerified?.();
      try {
        await refetchAffiliate();
      } catch {
        /* the banner already shows the result */
      }
    } catch {
      toast.error("That code did not match");
    } finally {
      setVerifying(false);
    }
  };

  if (state === "success") {
    return <DialogNotice tone="success">{email} is verified.</DialogNotice>;
  }

  return (
    <DialogNotice tone="warning">
      <form onSubmit={verify} className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>Verify {email} with the code we emailed you.</span>
        <span className="flex items-center gap-2">
          <Input
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="6-digit code"
            aria-label="Verification code"
            className="h-8 w-32 tabular-nums"
          />
          <Button type="submit" size="sm" variant="outline" disabled={verifying || code.length !== 6}>
            Verify
          </Button>
          <DialogTextButton onClick={() => void resend()} disabled={resending}>
            Resend code
          </DialogTextButton>
        </span>
      </form>
    </DialogNotice>
  );
}
